<template>
    <v-dialog scrollable v-model="show" width="80%" @input="onDialogChange">
        <v-card>
            <v-card-title>
                <span>{{ title }}</span>
            </v-card-title>

            <v-card-text>
                <v-container fluid>
                    <v-tabs v-model="testTab">
                        <v-tab>Payload</v-tab>
                        <v-tab>Result</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="testTab">
                        <!-- PAYLOAD TAB -->
                        <v-tab-item>
                            <div class="pt-3">
                                <p class="modal-label">Paste the Source Data Payload JSON below.</p>

                                <div class="textarea-wrapper">
                                    <div class="line-numbers" ref="testLineNumbers">
                                        <div v-for="n in testLineCount" :key="n">{{ n }}</div>
                                    </div>

                                    <v-textarea
                                        v-model="testJson"
                                        :disabled="isTesting"
                                        @input="onTestJsonChange"
                                        hide-details
                                        solo
                                        auto-grow
                                        class="custom-textarea"
                                        ref="testTextarea"
                                        rows="10"
                                    />
                                </div>
                            </div>
                        </v-tab-item>

                        <!-- RESULT TAB -->
                        <v-tab-item>
                            <div>
                                <div class="d-flex align-center mb-3">
                                    <div class="font-weight-medium">
                                        {{ testSummaryText }}
                                    </div>
                                </div>

                                <div v-if="!testResponse">
                                    <small class="text--secondary">Run a test to see results.</small>
                                </div>

                                <div v-else>
                                    <slot name="result" :response="testResponse" :result="testResult">
                                        <v-card outlined class="result-card">
                                            <v-card-text class="pa-3">
                                                <pre class="result-code">{{ testResult }}</pre>
                                            </v-card-text>
                                        </v-card>
                                    </slot>
                                </div>
                            </div>
                        </v-tab-item>
                    </v-tabs-items>
                </v-container>
            </v-card-text>

            <v-card-actions class="pb-3">
                <v-spacer></v-spacer>
                <v-btn small @click="closeDialog">Close</v-btn>
                <v-btn small color="primary" @click="onTest" :disabled="isTesting">
                    <v-progress-circular
                        v-if="isTesting"
                        indeterminate
                        size="12"
                        width="2"
                        color="green darken-3"
                        class="mr-2"
                    />
                    {{ isTesting ? 'Testing' : 'Test' }} {{ buttonLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { EventBus } from '@/js/event-bus.js'

export default {
    name: 'TestDataDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Test Data'
        },
        buttonLabel: {
            type: String,
            default: 'Data'
        },
        summaryText: {
            type: String,
            default: 'Results'
        }
    },
    data() {
        return {
            testTab: 0,
            testJson: '',
            testResult: '',
            testResponse: null,
            isTesting: false,
            testLineCount: 1
        };
    },
    computed: {
        show: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        testSummaryText() {
            if (!this.testResponse) return this.summaryText;
            return this.summaryText;
        }
    },
    methods: {
        syncTestTextAreaAndLineHeights() {
            const textarea = this.$refs.testTextarea ? this.$refs.testTextarea.$el.querySelector('textarea') : null;
            const lineNumbers = this.$refs.testLineNumbers;

            if (textarea && lineNumbers) {
                const newHeight = textarea.scrollHeight > 0 ? textarea.scrollHeight : 40;
                lineNumbers.style.height = `${newHeight}px`;
            }
        },
        updateTestLineCount() {
            this.testLineCount = (this.testJson || '').split('\n').length;
            this.$nextTick(this.syncTestTextAreaAndLineHeights);
        },
        onTestJsonChange() {
            this.testResult = '';
            this.testResponse = null;
            this.updateTestLineCount();
        },
        onDialogChange(val) {
            if (!val) {
                this.closeDialog();
            }
        },
        closeDialog() {
            this.show = false;
            this.testTab = 0;
            this.isTesting = false;
        },
        onTest() {
            let parsed;
            try {
                parsed = JSON.parse(this.testJson || '');
            } catch (e) {
                EventBus.$emit('show-error', 'Invalid JSON payload. Please fix the JSON and try again.');
                return;
            }

            this.isTesting = true;
            this.testResult = '';
            this.testResponse = null;

            // Emit test event with parsed data
            this.$emit('test', {
                payload: parsed,
                onSuccess: (response, result, summaryText) => {
                    this.testResponse = response;
                    this.testResult = result;
                    if (summaryText) {
                        this.$emit('update:summaryText', summaryText);
                    }
                    this.testTab = 1;
                    this.isTesting = false;
                },
                onError: (error) => {
                    this.testResponse = null;
                    this.testResult = '';
                    this.isTesting = false;
                }
            });
        }
    }
};
</script>

<style scoped>
.modal-label {
    font-weight: 500;
    margin-bottom: 12px;
}

.textarea-wrapper {
    position: relative;
    display: flex;
}

.line-numbers {
    flex-shrink: 0;
    width: 40px;
    padding: 12px 8px;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-right: none;
    border-radius: 4px 0 0 4px;
    text-align: right;
    color: #999;
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
    line-height: 1.5;
    overflow: hidden;
}

.line-numbers div {
    min-height: 21px;
}

.custom-textarea >>> textarea {
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
    line-height: 1.5;
    border-radius: 0 4px 4px 0 !important;
}

.custom-textarea >>> .v-input__control {
    border-radius: 0 4px 4px 0;
}

.result-card {
    background-color: #f5f5f5;
    max-height: 600px;
    overflow: auto;
}

.result-code {
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
    white-space: pre;
    color: #333;
}
</style>
